/**
 * What is checked about a machine export before any of it is sent.
 *
 * Both bounds are the API's, and the API refuses both on its own
 * (`413 VALIDATION_PAYLOAD_TOO_LARGE`, `422 MACHINE_IMPORT_TOO_MANY_ROWS`). They
 * are checked here as well so that a two-year export is turned back before a
 * slow counter connection spends a minute sending it.
 *
 * **The file is read as text only to count it.** What is sent is the `File`
 * itself: `File.text()` decodes and strips a leading BOM, so sending the text
 * would store bytes — and a `fileSha256` — that are not the machine's.
 */

/** 2 MiB, the upload routes' bound. */
export const MAX_FILE_BYTES = 2 * 1024 * 1024;

/** Data rows, header excluded. Lowered from 10,000 by the API's T015. */
export const MAX_DATA_ROWS = 5000;

/**
 * Data rows in a CSV, header excluded: line breaks outside quotes, blank lines
 * not counted. A quoted cell may hold a line break, and counting that as a row
 * would turn back a file the server accepts.
 */
export function countDataRows(text: string): number {
  let records = 0;
  let quoted = false;
  let blank = true;

  for (const char of text) {
    if (char === '"') quoted = !quoted;

    if (!quoted && (char === '\n' || char === '\r')) {
      if (!blank) records += 1;
      blank = true;
    } else if (char !== '﻿') {
      blank = false;
    }
  }

  if (!blank) records += 1;

  return Math.max(0, records - 1);
}

/** Why this file would be refused whole, or `null` if it may be sent. */
export async function problemWithFile(file: File): Promise<string | null> {
  if (file.size === 0) return 'That file is empty. Export ORDERS.CSV from the machine again.';

  if (file.size > MAX_FILE_BYTES) {
    return 'That file is larger than the 2 MB one upload accepts. Nothing was sent. Export a shorter period from the machine and upload each part separately.';
  }

  const rows = countDataRows(await file.text());

  if (rows > MAX_DATA_ROWS) {
    return `That file has ${rows.toLocaleString('en-IN')} rows, more than the 5,000 one upload accepts. Nothing was sent. Export a shorter period from the machine and upload each part separately.`;
  }

  return null;
}
